import React, { useMemo, useCallback } from 'react';
import { formatarPreco } from '../../utils/priceUtils';
import { useBookingStore } from '../../store/bookingStore';

const ProfessionalSelector = ({ 
  availableProfessionals, 
  selectedProfessional: propSelectedProfessional, 
  onProfessionalSelect, 
  professionalCustomPrices,
  primaryColor,
  selectedServices: propSelectedServices,
  loading,
  isAdmin = false
}) => {
  // Usar o store para obter estado e funções
  const {
    professional: storeProfessional,
    services: storeServices,
    setProfessional: storeSetProfessional
  } = useBookingStore();
  
  // Usar props ou valores do store
  const selectedProfessional = propSelectedProfessional !== undefined ? propSelectedProfessional : storeProfessional;
  const selectedServices = propSelectedServices || storeServices;
  
  // Handler que usa a função do store ou a prop onProfessionalSelect
  const handleProfessionalSelect = useCallback((profId) => {
    if (profId === selectedProfessional) return; // Evitar seleções redundantes
    
    if (onProfessionalSelect) {
      onProfessionalSelect(profId);
    } else {
      storeSetProfessional(profId);
    }
  }, [selectedProfessional, onProfessionalSelect, storeSetProfessional]);

  // Memoizar a função de renderização de preços para evitar cálculos desnecessários
  const renderCustomPriceInfo = useMemo(() => {
    return (professionalId) => {
      if (!professionalCustomPrices || !professionalCustomPrices[professionalId]) return null;
      
      const customPrices = professionalCustomPrices[professionalId];
      const services = Object.values(customPrices);
      
      if (services.length === 0) return null;
      
      // Se tiver um único serviço, mostrar direto
      if (services.length === 1) {
        const service = services[0];
        return (
          <div className="text-sm mt-1">
            <span className="font-medium">{service.serviceName}:</span> {formatarPreco(service.price)}
          </div>
        );
      }
      
      // Se tiver múltiplos serviços, mostrar totais
      return (
        <div className="text-sm mt-1">
          {services.map((service, index) => (
            <div key={index}>
              <span className="font-medium">{service.serviceName}:</span> {formatarPreco(service.price)}
            </div>
          ))}
        </div>
      );
    };
  }, [professionalCustomPrices]);

  // Definir classes para o modo admin vs cliente
  const getButtonClasses = (isSelected) => {
    const baseClasses = "rounded-lg py-2 px-4 transition-colors w-full flex items-center text-left";
    
    if (isSelected) {
      // Admin mode - azul claro com borda azul
      if (isAdmin) {
        return `${baseClasses} border-2 border-blue-500 bg-blue-50 text-blue-500`;
      }
      // Cliente mode - cor primária do salão
      return `${baseClasses} text-white`;
    }
    
    // Não selecionado - mesmo estilo independente do modo
    return `${baseClasses} bg-white border border-gray-300 text-gray-700 hover:bg-gray-50`;
  };

  return (
    <div className="mb-6 animate-fade-in">
      <h3 className="text-lg font-medium mb-3">Profissionais disponíveis:</h3>
      {loading ? (
        <div className="flex items-center justify-center w-full py-4">
          <p className="text-gray-500">Carregando profissionais disponíveis...</p>
        </div>
      ) : (
        <div className="space-y-3">
          {/* Mostrar "Qualquer profissional" apenas se houver mais de um disponível */}
          {availableProfessionals.length > 1 && (
            <button
              onClick={() => handleProfessionalSelect('qualquer')}
              className={getButtonClasses(selectedProfessional === 'qualquer')}
              style={selectedProfessional === 'qualquer' && !isAdmin ? { backgroundColor: primaryColor, borderColor: primaryColor } : {}}
            >
              <span className="flex-1">Qualquer profissional disponível</span>
              {selectedProfessional === 'qualquer' && (
                <span className={`ml-2 ${isAdmin ? 'text-blue-500' : 'text-white'}`}>✓</span>
              )}
            </button>
          )}
        
          {availableProfessionals.map(professional => (
            <button
              key={professional.id}
              onClick={() => handleProfessionalSelect(professional.id)}
              className={getButtonClasses(selectedProfessional === professional.id)}
              style={selectedProfessional === professional.id && !isAdmin ? { backgroundColor: primaryColor, borderColor: primaryColor } : {}}
            >
              <div className="flex w-full items-center">
                <span className="flex-1 font-medium">{professional.nome}</span>
                {selectedProfessional === professional.id && (
                  <span className={`ml-2 ${isAdmin ? 'text-blue-500' : 'text-white'}`}>✓</span>
                )}
              </div>
              {renderCustomPriceInfo(professional.id)}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default React.memo(ProfessionalSelector);
